// Ported verbatim from microsites/womens-conference/src/lib/conference.ts.
/**
 * The generic conference shape.
 *
 * Everything in this file is deliberately event-agnostic: a conference is a
 * registration window, a venue, some speakers, a schedule, and a set of
 * capped choices each attendee makes. What Matters fills this shape in
 * `what-matters.ts`; a Hope 365 site next year fills it with different data
 * and a different palette without touching the components that render it.
 *
 * The MP grounding, so nothing here invents a storage model:
 *  - Choices are MP product option groups (`Product_Option_Groups` +
 *    `Product_Option_Prices`), mutually exclusive per group, capacity in
 *    `Max_Qty`, each option wired to a check-in group via `Add_to_Group`.
 *  - An attendee's picks are `Invoice_Detail` rows on their
 *    `Event_Participant_ID`.
 *  - Free-text answers (campus, meal, how-did-you-hear) are
 *    `Form_Response_Answers` on the event's registration form.
 *  - The schedule maps to MP's `Agenda_Elements` (Event_ID, Sequence,
 *    Element_Title, Description, Start, End) plus fields that table does not
 *    have yet: day, room, kind, and a link back to the choice group a
 *    breakout row represents. Those extras are typed here so a future MP
 *    extension (or companion table) has a spec to match.
 */

/**
 * The five lives of the page, in the order one conference lives them.
 *
 * `teaser` is the fifth and it is really the first of the NEXT one: once the
 * team puts another year on the calendar, the recap stops being the front page
 * and becomes something you scroll to. That is the whole "living page" idea —
 * nobody edits a phase in, the clock moves and the page follows.
 */
export type ConferencePhase = 'registration' | 'preEvent' | 'live' | 'recap' | 'teaser';

/**
 * The one rule about registration buttons: never offer a seat that cannot be
 * taken.
 *
 * Exactly one phase sells seats, and it is not a coincidence — `preEvent`
 * begins the instant registration closes, so anything past `registration` is
 * past the window by definition. Every CTA that opens the register sheet asks
 * this rather than listing phases itself, because a phase list is a rule
 * written down four times, and the fourth copy is the one that keeps selling
 * seats at a conference that already happened.
 */
export function registrationOpen(phase: ConferencePhase, capacity?: SeatCount | null): boolean {
  if (phase !== 'registration') return false;
  // Sold out is the second way a sign-up button can be a lie, and the one the
  // clock cannot see. Unknown counts do NOT close the door: a capacity read
  // that failed should cost her a badge, not her registration.
  if (capacity && capacity.limit !== null && capacity.taken >= capacity.limit) return false;
  return true;
}

export interface SeatCount {
  taken: number;
  /** Null when the gathering has no cap, or when we could not read one. */
  limit: number | null;
}

/**
 * When a choice stops being changeable.
 *
 * Three layers, narrowest first, which is the shape of the promise: this one
 * choice has a caterer behind it, or the whole gathering has a house rule, or
 * nothing does and it stays open until the doors. Migration 0122 is where the
 * two dates live and why they are separate.
 */
export function choicesOpen(
  deadline: { groupCloseAt?: string | null; gatheringCloseAt?: string | null },
  now: Date
): boolean {
  const at = deadline.groupCloseAt ?? deadline.gatheringCloseAt ?? null;
  if (!at) return true;
  return now < new Date(at);
}

/**
 * "Locked on October 30" — or, when the team wrote one, their reason instead.
 *
 * Deliberately a date and not a countdown. A countdown is for something she can
 * still act on; this has already happened to her, and the only useful thing it
 * can say is when, so she knows whether to ask.
 */
export function lockedCopy(
  deadline: {
    groupCloseAt?: string | null;
    gatheringCloseAt?: string | null;
    reason?: string | null;
  },
  timeZone: string
): string {
  if (deadline.reason) return deadline.reason;
  const at = deadline.groupCloseAt ?? deadline.gatheringCloseAt ?? null;
  if (!at) return 'Locked';
  const when = new Date(at).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone,
  });
  return `Locked on ${when}`;
}

export interface Speaker {
  key: string;
  name: string;
  /** One line under the name: campus, credential, or role. */
  tagline: string;
  /** MP Contact_ID when the speaker is a person in MP. */
  contactId?: number;
  /** Filled at request time from the MP contact's default image. */
  imageGuid?: string | null;
  /** A bundled portrait (public/ path) for speakers with no MP photo. */
  imageSrc?: string;
  /**
   * The wide photograph for a hero card.
   *
   * A DIFFERENT shot from `imageSrc` rather than a larger one: `imageSrc` is a
   * face crop for 34px circles, and stretching it into a 4:3 card is not the
   * same picture. `KeynoteCard` hardcoded this for a year because there was
   * nowhere to put it. See migration 0146.
   */
  cardImage?: string;
  /** The speaker's own site, linked wherever there is room. */
  url?: string;
  /** Longer bio, only rendered where there is room for it. */
  bio?: string;
  /** External guests have no MP contact photo; art comes from the team. */
  external?: boolean;
  /** Credentials and history: the sheet's second paragraph, not the card's. */
  background?: string;
  /** Published works, shown as covers where a sheet has room. */
  books?: SpeakerBook[];
}

export interface SpeakerBook {
  title: string;
  /** The line under the title on the cover, when it has one. */
  subtitle?: string;
  /** Cover art (public/ path). */
  cover?: string;
  /** True when the art is the bare cover, edge to edge. Publisher tiles
   *  usually arrive matted with a shadow already painted in; those don't
   *  want a second one. */
  bleed?: boolean;
  /** Where to buy or read more. */
  url?: string;
}

/** One slot of a capped, mutually exclusive choice (a breakout in one session). */
export interface TopicSlot {
  /** Which choice group this slot belongs to (session 1, session 2...). */
  slot: number;
  /** MP Product_Option_Price_ID: the thing an attendee actually picked. */
  optionPriceId: number;
  /** MP Max_Qty for that option. */
  capacity: number;
  /** MP Add_to_Group: the check-in group registrants land in. */
  groupId: number;
}

/**
 * The recorded breakout.
 *
 * Every breakout is filmed before the conference, which changes what a recap
 * can be: the content exists in October, so the "watch it again" page is not
 * waiting on an editor in November. It also means a woman who had to choose
 * between two sessions she wanted can have both, and one who never came at all
 * still has something to be handed.
 *
 * `points` is the part people actually ask for — the notes they meant to take.
 * Written by whoever previews the recording, not transcribed.
 */
export interface BreakoutReplay {
  /** The recording. A file URL, an MP file GUID's URL, or an embed. */
  videoUrl?: string;
  /** Still frame; falls back to the conference art when absent. */
  posterSrc?: string;
  durationMinutes: number;
  /** What she said, in her order. Four or five, not a transcript. */
  points: string[];
  /** Passages she taught from, as she cited them. */
  scripture?: string[];
  /** Her handout, when she brought one. */
  handoutUrl?: string;
}

export interface BreakoutTopic {
  slug: string;
  /** The full "Name: what it is about" string. */
  title: string;
  /** Fits on a card or a chip without wrapping three times. */
  shortTitle: string;
  /** The half after the colon, absent on a name-only title. */
  subtitle?: string;
  speakerKey: string;
  blurb: string;
  slots: TopicSlot[];
  /** A hosted panel reads differently from a taught session. */
  panel?: boolean;
  /** Placeholder room until rooms are assigned in MP. */
  room?: string;
  /** Present once the session has been recorded and previewed. */
  replay?: BreakoutReplay;
}

/**
 * What a block on the agenda IS.
 *
 * `travel` and `reception` came off Misty's own run of show — the walking
 * between rooms and the dessert afterwards. They are on the sheet because a
 * schedule that jumps from breakout straight to lunch implies you can be in
 * both, but `travel` is rendered quieter than the rest: a gap is not a
 * session.
 *
 * `break` is rendered exactly as quietly and is a separate kind anyway, because
 * only `travel` means she has to be somewhere else when it ends. Friday's
 * twenty minutes are in the room she is already sitting in, and calling that
 * travel sends four hundred women looking for a hallway (0143).
 */
export type AgendaKind =
  | 'doors'
  | 'worship'
  | 'session'
  | 'breakout'
  | 'meal'
  | 'close'
  | 'travel'
  | 'break'
  | 'reception';

export interface AgendaItem {
  /** Key into ConferenceConfig.days. */
  day: string;
  /** Maps to Agenda_Elements.Sequence. */
  sequence: number;
  /** Maps to Agenda_Elements.Element_Title. */
  title: string;
  /** Maps to Agenda_Elements.Description. */
  description?: string;
  /** Wall-clock at the venue, 24h "HH:MM". Maps to Agenda_Elements.Start/End. */
  start: string;
  end?: string;
  kind: AgendaKind;
  /** For kind 'breakout': which choice slot this block is. */
  slot?: number;
  /** Not in Agenda_Elements yet; carried here so the UI is ready for it. */
  room?: string;
  speakerKey?: string;
  /**
   * Which ways in may see this block. Empty or absent means everybody, which is
   * every block on every gathering today. See `agendaFor`.
   */
  audienceTypeIds?: number[];
  /**
   * The recording, for the main-stage sessions.
   *
   * The breakouts are filmed ahead of time; the sessions from the front are
   * filmed live, so these arrive a few days later. Same shape either way, which
   * is why the recap can show sessions and breakouts as one kind of card.
   */
  replay?: BreakoutReplay;
}

export interface ConferenceDay {
  key: string;
  /** "Friday" */
  label: string;
  /** ISO date at the venue, e.g. "2026-11-06". */
  date: string;
}

export interface LunchOption {
  /** Exact Form_Response_Answers.Response string: matching is equality. */
  value: string;
  label: string;
  detail?: string;
  tags?: string[];
  /**
   * The caterer's own product photo, under `public/art/lunch/`.
   *
   * Optional on purpose, and not because the menu is uncertain: a card whose
   * file is missing falls back to `mark` at runtime, so dropping in or pulling
   * out a photo is a file operation with no code change and no broken image.
   */
  photo?: string;
  /** The drawn stand-in, used wherever there is no photo and anywhere too
   *  small to read one. Every option keeps a mark for that reason. */
  mark?: 'sandwich' | 'salad' | 'care';
}

/**
 * Who is actually making the food.
 *
 * Named, credited, and shown with their own pictures. This site is not an
 * ordering service: it collects a group order that someone on staff phones in,
 * so "Panera" and a photograph of Panera's sandwich describe what will be in
 * the box. That is a statement about the food, not a claim of partnership,
 * which is why the credit reads "Catered by" and never sits near our logo.
 */
export interface Caterer {
  name: string;
  /** Their wordmark, under `public/art/lunch/`. Missing is fine: the credit
   *  degrades to the name set in type. */
  logo?: string;
  /** Where the assets came from, for whoever refreshes them next. */
  assetsFrom?: string;
}

/**
 * A photograph of the weekend.
 *
 * `shape` is layout, not metadata: the mosaic reads as a scrapbook because the
 * frames are different sizes, and which photo deserves the big frame is a
 * judgement the team makes, not something a grid can guess.
 */
export interface RecapPhoto {
  src: string;
  /** Real alt text. A gallery of "" is a gallery a blind woman cannot attend. */
  alt: string;
  caption?: string;
  shape?: 'wide' | 'tall' | 'square';
  /** Who took it, when that matters. */
  credit?: string;
}

export type SurveyQuestionKind = 'rating' | 'pickTopic' | 'text' | 'yesNo';

export interface SurveyQuestion {
  key: string;
  prompt: string;
  kind: SurveyQuestionKind;
  /** Shown under the prompt when the question needs a sentence of framing. */
  hint?: string;
  /** Everything except the first question, in practice: a survey that demands
   *  five answers gets three. */
  optional?: boolean;
}

/**
 * The card that comes up on the recap page.
 *
 * Not a link to a Google Form. The team texts a link to the site, she lands on
 * the page she already knows, and the ask sits on top of the photos she came
 * to look at. Four questions, one screen, no account.
 */
export interface RecapSurvey {
  /**
   * No title and no intro on purpose. The card leads with the question, which
   * is the only thing being asked for — a heading above it would just be a
   * sentence she has to read before she can answer.
   */
  questions: SurveyQuestion[];
  /** Wait this long into the visit before asking. Reading first, then asked. */
  delaySeconds: number;
  thanks: string;
}

export interface RecapContent {
  /**
   * What the masthead says once it is over, under "Thank you for coming".
   *
   * Written by the team on the Monday, in sentences, about what actually
   * happened in the room. It is deliberately not built out of numbers: nobody
   * remembers a weekend as a headcount.
   */
  thanks?: string;
  /** The line the weekend gets remembered by. */
  headline: string;
  blurb?: string;
  /** Two or three, never a wall: once there are photographs, numbers are
   *  seasoning. */
  numbers?: { value: string; label: string }[];
  photos: RecapPhoto[];
  survey?: RecapSurvey;
  /**
   * Where a woman goes from here.
   *
   * The recap's one call to action, and it is deliberately not "read this page
   * again": she is on the page. What she can actually do next is the next thing
   * the church is doing, which in November is Christmas. Config, because that
   * answer changes every season and should never be a deploy.
   */
  next?: { label: string; url: string };
}

/**
 * Next year, once it exists.
 *
 * The moment this is filled in with a `teaserFrom` in the past, the page stops
 * leading with the weekend that happened and starts leading with the one that
 * is coming — the same page, a different personality, no deploy. Everything
 * here is prose on purpose: at teaser time there is no MP event yet, no
 * product, no form, and pretending otherwise would mean an event id that
 * points at nothing.
 */
export interface NextEdition {
  /** "2028", or a new name if the conference gets one. */
  label: string;
  /** When the page flips from recap to coming-soon (ISO with offset). */
  teaserFrom: string;
  /** The one line the coming-soon page leads with. */
  headline: string;
  /** "November 3 and 4, 2028" — or "Next fall" when that is all anyone knows. */
  dates?: string;
  venue?: string;
  /** ISO instant when registration opens, if it is on the calendar. Drives the
   *  only countdown a teaser page is allowed to show. */
  registrationOpens?: string;
  /** The honest caveat under the headline. */
  note?: string;
}

export interface ConferenceConfig {
  /** Display identity */
  name: string;
  /** The ministry mark set in script above the name. */
  ministryMark: string;
  tagline: string;
  /** The line the weekend turns on, set large. The event's own words. */
  invitation: string;
  /** What the weekend is, in the event's own words. Shown where somebody is
   *  deciding whether to come, not in the masthead. Deliberately excludes the
   *  registration rules, the prices and the breakout list: this app covers all
   *  three better than a paragraph can. */
  description: string;
  church: string;
  churchUrl: string;
  /** Where "home" points until this subdomain grows its own landing page. */
  ministryUrl: string;

  /** MP wiring */
  eventId: number;
  productId: number;
  registrationFormId: number;
  basePrice: number;
  /** Form_Field_IDs for the answers the profile shows. */
  formFields: { campus: number; heardAbout: number; lunch: number };
  /** Questions that belong to the registration, not to each registrant.
   *  "How did you hear about the conference?" is answered by whoever is
   *  signing up; asking her friend the same question is noise. Stored per
   *  person in MP all the same, because that is where MP keeps answers. */
  partyScopedFieldIds?: number[];
  /** Product_Option_Group_IDs for the breakout choice groups, by slot. */
  breakoutGroups: { slot: number; groupId: number; label: string; agendaHint: string }[];
  /** Option lines that are checkout mechanics, not things a person bought
   *  (e.g. an "I Understand" disclaimer option). Hidden from the ticket. */
  hiddenOptionPriceIds?: number[];

  /** Time, all wall-clock at the venue */
  timezone: string;
  days: ConferenceDay[];
  /** Instants for phase math (ISO with offset). */
  registrationOpens: string;
  registrationCloses: string;
  eventStarts: string;
  eventEnds: string;

  venue: {
    name: string;
    address: string;
    city: string;
    mapUrl: string;
    /** MP's own geocode for the location; no map without it. */
    lat?: number;
    lng?: number;
  };

  /**
   * Who answers questions from the site. Hard-coded on purpose: MP's own
   * contact on event 329668 is somebody else, registration is live, and
   * changing the event's contact is a conversation with the team rather than
   * a code change. Swap this for a read of the event's Primary_Contact once
   * MP agrees with reality.
   */
  host: { contactId: number; name: string; role: string };

  keynoteKey: string;
  speakers: Speaker[];
  topics: BreakoutTopic[];
  lunchOptions: LunchOption[];
  /** Absent means lunch is described but not attributed to anyone. */
  caterer?: Caterer;
  agenda: AgendaItem[];
  /** True until the schedule is real (it is drafted from the event copy). */
  agendaIsDraft: boolean;

  /** What the recap phase shows. Absent means the honest placeholder. */
  recap?: RecapContent;
  /** Next year. Absent means the recap stays the front page. */
  nextEdition?: NextEdition;
}

export function speakerByKey(config: ConferenceConfig, key: string): Speaker | undefined {
  return config.speakers.find((s) => s.key === key);
}

export function topicForOption(
  config: ConferenceConfig,
  optionPriceId: number
): { topic: BreakoutTopic; slot: TopicSlot } | undefined {
  for (const topic of config.topics) {
    const slot = topic.slots.find((s) => s.optionPriceId === optionPriceId);
    if (slot) return { topic, slot };
  }
  return undefined;
}

/** Coarse fullness states, on purpose: exact seat counts read as either
 *  panic or embarrassment. Thresholds follow the common conference-software
 *  convention (worn in by Sched): "almost full" fires at 75%. */
export type FullnessState = 'open' | 'almostFull' | 'full';

export function fullness(picked: number, capacity: number): FullnessState {
  if (picked >= capacity) return 'full';
  if (picked >= capacity * 0.75) return 'almostFull';
  return 'open';
}
